import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import store from 'store2'
import { useDueNow } from '../../../../hooks'
import { showLoader } from '../../../../actions/main-actions'
import { getAffiliateId, parseQuery } from '../../../../utils'
import useCurrentShop from '../../hooks/useCurrentShop'
import useTrip from '../../hooks/useTrip'
import useCheckoutSummary from '../../hooks/useCheckoutSummary'
import useCheckoutForm from '../../hooks/useCheckoutForm'
import useCheckoutEffects from './hooks/useCheckoutEffects'
import IntentPopup from '../../../checkout/IntentPopup'
import ProductTitleCard from './ProductTitleCard/ProductTitleCard'
import StepsProgressBar from './StepsProgressBar/StepsProgressBar'
import SingleOccupancyCard from './SingleOccupancyCard/SingleOccupancyCard'
import TripSummaryCard from './TripSummaryCard/TripSummaryCard'
import WhyDiveWithPadiCard from './WhyDiveWithPadiCard/WhyDiveWithPadiCard'
import ContactDetailsCard from './ContactDetailsCard/ContactDetailsCard'
import ReserveNowPayLaterStrip from './ReserveNowPayLaterStrip/ReserveNowPayLaterStrip'
import styles from './Step4.module.scss'

const query = parseQuery()
const affiliateId = query.aid || getAffiliateId()?.aid
const referrer = query.referrer || store.session('diviac.referrer')
const uuid = query.order_uuid || store.session('diviac.order_uuid')

if(uuid) {
  store.session('diviac.order_uuid', uuid)
}
if(referrer) {
  store.session('diviac.referrer', referrer)
}

Step4.propTypes = {
  tripId: PropTypes.string,
  setOrder: PropTypes.func,
  countryCode: PropTypes.string,
  url: PropTypes.string,
}

export default function Step4({ tripId, setOrder, countryCode, url }) {
  const dispatch = useDispatch()
  const { shopInfo } = useCurrentShop()
  const { dueNowData, getDueNowData } = useDueNow()
  const { selectedTrip, requirements } = useTrip()
  const {
    guestsSplitting,
    checkoutInclusions,
    roomParams,
    totalPrice,
  } = useSelector(({
    guestsSplitting,
    checkoutInclusions,
    roomParams,
    totalPrice,
  }) => ({
    guestsSplitting,
    checkoutInclusions,
    roomParams,
    totalPrice,
  }))

  const { checkoutSummary } = useCheckoutSummary(
    dueNowData,
    selectedTrip,
    shopInfo,
    roomParams,
    checkoutInclusions,
  )

  const { sendForm } = useCheckoutForm(
    uuid,
    referrer,
    dueNowData,
    setOrder,
    requirements,
    selectedTrip?.duration,
    roomParams?.divers,
    affiliateId,
  )

  useCheckoutEffects({
    shopInfo,
    checkoutInclusions,
    selectedTrip,
    guestsSplitting,
    totalPrice,
    dueNowData,
    getDueNowData,
    roomParams,
    tripId,
  })

  const onVoucherAdd = (coupon) => {
    dispatch(showLoader(true))
    getDueNowData(shopInfo.id, dueNowData.selectedInclusions, {
      ...selectedTrip,
      roomParams,
    }, coupon.id).then(() => {
      dispatch(showLoader(false))
    })
  }

  const onCouponRemove = () => {
    dispatch(showLoader(true))
    getDueNowData(shopInfo.id, dueNowData.selectedInclusions, selectedTrip).then(() => {
      dispatch(showLoader(false))
    })
  }

  if(!selectedTrip || !checkoutSummary || !shopInfo) {
    return null
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.contentBody}>
        <ProductTitleCard order={checkoutSummary} url={url} />
        <StepsProgressBar currency={shopInfo.currency} />
        <SingleOccupancyCard checkoutSummary={checkoutSummary} />
        <TripSummaryCard
          order={checkoutSummary}
          onCouponRemove={onCouponRemove}
          onVoucherAdd={onVoucherAdd}
          dueNowData={dueNowData}
        />
        <WhyDiveWithPadiCard freeCancellationDays={selectedTrip.freeCancellationDays} />
        <ContactDetailsCard
          onCheckoutFormSubmit={sendForm}
          shopKind={shopInfo.kind}
          isEnquiry
          orderEdit={!!uuid}
          selectedTripData={selectedTrip}
          countryCode={countryCode}
        />
        <ReserveNowPayLaterStrip />
      </div>
      <IntentPopup
        isLiveabord
        shopTitle={shopInfo.title}
        itineraryTitle={selectedTrip?.itinerary?.title}
        dateFrom={selectedTrip.dateFrom}
        dateTo={selectedTrip.dateTo}
        dueNowData={dueNowData?.payload}
        guestsSplitting={guestsSplitting}
        countryCode={countryCode}
      />
    </div>
  )
}
